import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../contexts/authContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { setCurrentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for invitation and redirect after login
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const inviteId = urlParams.get('inviteId');
    
    if (inviteId) {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          navigate(`/accept-invitation?inviteId=${inviteId}`);
        }
      });
      
      return () => unsubscribe();
    }
  }, [location.search, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setCurrentUser({
          ...user,
          ...userData,
          id: userDoc.id,
          appID: userData.appID,
        });
        
        const urlParams = new URLSearchParams(location.search);
        const inviteId = urlParams.get('inviteId');
        
        if (inviteId) {
          navigate(`/accept-invitation?inviteId=${inviteId}`);
        } else {
          navigate('/dashboard');
        }
      } else {
        console.error('User data not found in Firestore');
        setError('User data could not be found. Please contact support.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid email or password');
    }
  };

  return (
    <div className="min-h-screen relative bg-gray-900">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://i0.wp.com/togetheronmission.org/wp-content/uploads/2016/11/cropped-group_like.jpg?fit=1286%2C857&ssl=1"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gray-900/35"></div>
      </div>

      {/* Login Card - glass with transparency to show background */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <div className="relative w-full max-w-md">
          {/* Blur layer: directly over background so backdrop-filter captures the image */}
          <div
            className="absolute inset-0 rounded-2xl overflow-hidden backdrop-blur-xl"
            aria-hidden="true"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          />
          {/* Content layer: semi-transparent so the blurred layer shows through */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/25 ring-1 ring-inset ring-white/20">
            {/* Card Header - darker gray with image still showing through */}
            <div
              className="px-8 pt-8 pb-6 text-center"
              style={{ background: 'rgba(17,24,39,0.78)' }}
            >
              <img
                src="/images/TOM-logo-512.png"
                alt="Together On Mission"
                className="h-14 w-auto mx-auto mb-4"
              />
              <h2 className="text-2xl font-bold text-white mb-1">
                Pick Up Where You Left Off
              </h2>
              <p className="text-gray-300 text-sm">
                Someone out there is waiting to hear from you.
              </p>
            </div>

            {/* Card Body - semi-transparent so blurred image shows through */}
            <div className="px-8 py-8" style={{ background: 'rgba(0,0,0,0.2)' }}>
              {error && (
                <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                  {error}
                </div>
              )}
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition"
                    required
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <Link to="/forgot-password" className="text-xs text-primary hover:text-primary/80 font-medium">
                      Forgot password?
                    </Link>
                  </div>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-base font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  Sign In
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-400/60"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 text-gray-300" style={{ background: 'rgba(17,24,39,0.95)' }}>New to Together On Mission?</span>
                </div>
              </div>

              <Link
                to="/signup"
                className="w-full flex justify-center py-3 px-4 border-2 border-white rounded-xl text-base font-semibold text-white hover:bg-white hover:text-gray-900 transition-colors"
              >
                Create an Account
              </Link>
            </div>
          </div>

          {/* Subtle tagline below card */}
          <p className="text-center text-white/50 text-xs mt-6">
            Go make disciples. &mdash; Matthew 28:19
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
